import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { OrganizationProvider } from "@/contexts/OrganizationContext";
import { SessionTimeoutProvider } from "@/components/SessionTimeoutProvider";
import { InsightDialogProvider } from "@/hooks/useInsightDialog";
import ProtectedRoute from "@/components/ProtectedRoute";

// Landing is eagerly loaded (initial route)
import Landing from "./pages/Landing";

// Lazy-load all other pages to reduce initial bundle
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Campaigns = lazy(() => import("./pages/Campaigns"));
const CampaignDetail = lazy(() => import("./pages/CampaignDetail"));
const MetaCampaignDetail = lazy(() => import("./pages/MetaCampaignDetail"));
const UnifiedCampaignsPage = lazy(() => import("./pages/UnifiedCampaigns").then(m => ({ default: m.default })));
const UnifiedCampaignDetailPage = lazy(() => import("./pages/UnifiedCampaigns").then(m => ({ default: m.UnifiedCampaignDetailPage })));
const Advisor = lazy(() => import("./pages/Advisor"));
const Financing = lazy(() => import("./pages/Financing"));
const Settings = lazy(() => import("./pages/Settings"));

const Help = lazy(() => import("./pages/Help"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Contact = lazy(() => import("./pages/Contact"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Backoffice pages
const BackofficeDashboard = lazy(() => import("./pages/backoffice/BackofficeDashboard"));
const UserManagement = lazy(() => import("./pages/backoffice/UserManagement"));
const OrganizationManagement = lazy(() => import("./pages/backoffice/OrganizationManagement"));
const CampaignManagement = lazy(() => import("./pages/backoffice/CampaignManagement"));
const CapitalManagement = lazy(() => import("./pages/backoffice/CapitalManagement"));
const BackofficeReporting = lazy(() => import("./pages/backoffice/BackofficeReporting"));
const BackofficeSettings = lazy(() => import("./pages/backoffice/BackofficeSettings"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <SessionTimeoutProvider>
            <OrganizationProvider>
              <InsightDialogProvider>
              <Suspense fallback={null}>
              <Routes>
              {/* Public routes */}
              <Route path="/" element={<Landing />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/help" element={<Help />} />
              
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/contact" element={<Contact />} />
              
              {/* Onboarding (protected but skips org check to avoid circular redirect) */}
              <Route path="/onboarding" element={
                <ProtectedRoute skipOrgCheck>
                  <Onboarding />
                </ProtectedRoute>
              } />
              
              {/* Protected client routes */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/campaigns" element={
                <ProtectedRoute>
                  <Campaigns />
                </ProtectedRoute>
              } />
              <Route path="/campaigns/:id" element={
                <ProtectedRoute>
                  <CampaignDetail />
                </ProtectedRoute>
              } />
              <Route path="/campaigns/meta/:id" element={
                <ProtectedRoute>
                  <MetaCampaignDetail />
                </ProtectedRoute>
              } />
              <Route path="/campaigns/unified" element={
                <ProtectedRoute>
                  <UnifiedCampaignsPage />
                </ProtectedRoute>
              } />
              <Route path="/campaigns/unified/:id" element={
                <ProtectedRoute>
                  <UnifiedCampaignDetailPage />
                </ProtectedRoute>
              } />
              <Route path="/advisor" element={
                <ProtectedRoute>
                  <Advisor />
                </ProtectedRoute>
              } />
              <Route path="/financing" element={
                <ProtectedRoute>
                  <Financing />
                </ProtectedRoute>
              } />
              <Route path="/settings" element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              } />
              
            {/* Protected backoffice routes (admin only) */}
            <Route path="/backoffice" element={
              <ProtectedRoute requireAdmin>
                <BackofficeDashboard />
              </ProtectedRoute>
            } />
            <Route path="/backoffice/organizations" element={
              <ProtectedRoute requireAdmin>
                <OrganizationManagement />
              </ProtectedRoute>
            } />
            <Route path="/backoffice/campaigns" element={
              <ProtectedRoute requireAdmin>
                <CampaignManagement />
              </ProtectedRoute>
            } />
            <Route path="/backoffice/capital" element={
              <ProtectedRoute requireAdmin>
                <CapitalManagement />
              </ProtectedRoute>
            } />
            <Route path="/backoffice/reporting" element={
              <ProtectedRoute requireAdmin>
                <BackofficeReporting />
              </ProtectedRoute>
            } />
            <Route path="/backoffice/users" element={
              <ProtectedRoute requireAdmin>
                <UserManagement />
              </ProtectedRoute>
            } />
            <Route path="/backoffice/settings" element={
              <ProtectedRoute requireAdmin>
                <BackofficeSettings />
              </ProtectedRoute>
            } />
              
                <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
              </InsightDialogProvider>
            </OrganizationProvider>
          </SessionTimeoutProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
